import {
    type Dispatch,
    type SetStateAction,
    useState,
    type MouseEvent,
} from 'react'
import { useAtom } from 'jotai'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import Button from '@mui/material/Button'

import { DiscountType, type YearlyFeeDiscountAssignInMunicipalityResponse, type YearlyFeeDiscount } from '@/types'
import { ROUTE_SEGMENTS } from '@/constants/general'
import { useYearlyFeeDiscountAssignInMunicipality } from '@/hooks/api/use-yearly-fee-discount'
import { discountAssignInMunicipalityJobIdAtom } from '@/state'

function YearlyFeeDiscountActions({
    yearlyFeeDiscount,
    setRemove,
    setError,
    setEdit,
    allowDetail,
    largeButton,
}: {
    yearlyFeeDiscount: YearlyFeeDiscount,
    setRemove: Dispatch<SetStateAction<YearlyFeeDiscount>>,
    setError: Dispatch<SetStateAction<string | null>>,
    setEdit?: Dispatch<SetStateAction<boolean>>,
    allowDetail?: boolean,
    largeButton?: boolean,
}) {

    const [requestInProgress, setRequestInProgress] = useState<boolean>(false)
    const [discountAssignInMunicipalityJobId, setDiscountAssignInMunicipalityJobId] = useAtom(discountAssignInMunicipalityJobIdAtom)
        

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const successCallback = (data: YearlyFeeDiscountAssignInMunicipalityResponse) => {
        if (data.id) {
            setDiscountAssignInMunicipalityJobId(data.id)
        }
    }

    const mutation = useYearlyFeeDiscountAssignInMunicipality(
        {
            onSuccessCallback: successCallback,
            setRequestInProgress,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            },
        },
    )

    const handleClickAssignInMunicipality = () => {
        setRequestInProgress(true)
        mutation.mutate(yearlyFeeDiscount.id)
    }

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(`${window.location.origin}/${ROUTE_SEGMENTS.base}/${ROUTE_SEGMENTS.municipalityManagement
            }/${ROUTE_SEGMENTS.yearlyFeeCollection}/${ROUTE_SEGMENTS.yearlyFeeCollectionDiscounts}/${yearlyFeeDiscount.id}`)
    }

    return (
        <>
            {largeButton ?
                <Button
                    variant="outlined"
                    aria-label="actions"
                    aria-controls={open ? 'actions-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                    color="terciary"
                    startIcon={<MoreVertRoundedIcon />}
                    onClick={handleClick}
                    sx={{
                        backgroundColor: 'customGrey.boxBackground',
                    }}
                >
                    Více možností
                </Button>
                :
                <Tooltip title="Více možností">
                    <IconButton
                        id="actions-button"
                        aria-label="actions"
                        aria-controls={open ? 'actions-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="true"
                        color="terciary"
                        onClick={handleClick}
                        // sx={{ backgroundColor: 'editableOrange.main' }}
                    >
                        <MoreVertRoundedIcon />
                    </IconButton>
                </Tooltip>
            }
            <Menu
                id="actions-menu"
                MenuListProps={{
                    'aria-labelledby': 'actions-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={(e: MouseEvent) => {
                    e?.stopPropagation()
                    handleClose()
                }}
                slotProps={{
                    paper: {
                        style: {
                            minWidth: '15ch',
                        }
                    }
                }}
            >
                {allowDetail &&
                    <MenuItem>
                        <ListItemIcon>
                            <VisibilityRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        Detail slevy
                    </MenuItem>
                }
                {setEdit &&
                    <MenuItem
                        onClick={e => {
                            e.stopPropagation()
                            setEdit(true)
                            setAnchorEl(null)
                        }}
                        disabled={requestInProgress}
                    >
                        <ListItemIcon>
                            <EditRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        Upravit
                    </MenuItem>
                }
                <MenuItem
                     onClick={e => {
                        e.stopPropagation()
                        handleCopyToClipboard()
                        setAnchorEl(null)
                    }}
                >
                    <ListItemIcon>
                        <LinkRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    Zkopírovat odkaz
                </MenuItem>
                {(yearlyFeeDiscount.type === DiscountType.age || yearlyFeeDiscount.type === DiscountType.inMotivationProgram) &&
                    <MenuItem
                        onClick={e => {
                            e.stopPropagation()
                            handleClickAssignInMunicipality()
                            setAnchorEl(null)
                        }}
                        disabled={requestInProgress || !!discountAssignInMunicipalityJobId}
                    >
                        <ListItemIcon>
                            <CheckCircleRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        Aplikovat v celé obci
                    </MenuItem>
                }
                <MenuItem
                    onClick={e => {
                        e.stopPropagation()
                        setRemove(yearlyFeeDiscount)
                        setAnchorEl(null)
                    }}
                >
                    <ListItemIcon>
                        <DeleteRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    Smazat
                </MenuItem>
            </Menu>
        </>
    )
}

export default YearlyFeeDiscountActions
